import { router, usePage } from '@inertiajs/react'
import type { LocaleSetting, SharedProps } from '@/types'
import { t } from '@/lib/i18n'

// 语言名一律用该语言自己的写法（中文 / English），不随界面语言翻译——
// 一个只懂英文的用户在中文界面里也得认出哪个是英文。「自动」是个功能说明而非语言名，所以要翻。
//
// 标签写成字面量 t() 而不是先存 key 再 t(key)：test/i18n_client_keys_test.rb 靠静态扫描
// TSX 里的 t('…') 来判断哪些 key 有人用，间接引用会让它误判成死 key。
const ALL_OPTIONS: { value: LocaleSetting; label: () => string }[] = [
  { value: 'auto', label: () => t('account.language_auto') },
  { value: 'zh-CN', label: () => t('account.language_zh') },
  { value: 'en', label: () => t('account.language_en') },
]

/**
 * 三选语言切换。登录后写用户设置，未登录只写 cookie——两种情况都由 LocalesController 处理。
 *
 * 注意服务端会回一个整页重载（inertia_location）而不是 SPA 换页：译文目录内联在 layout 里，
 * 不整页重载的话内存里还是旧目录，页面会中英混排。也因此点完不用自己收起菜单——整页重开了。
 *
 * 两种呈现都带 `aria-pressed`：pills 那一版的选中态原来**只用颜色表达**
 * （`bg-tint text-brand border-brand`），屏幕阅读器读不出「现在选的是哪个」。
 * menu 那一版有个 ✓ 是文本、读得出来，但两处给同一个属性才不会再漂。
 * 同 components/ui.tsx 的 `Chip`。顺带让系统测试能断言语义而不是断言类名 ——
 * 那条测试原来钉的是 `border-[#5b7cfa]`，S0 把 hex 换成 token 之后它就一直红着。
 *
 * 两种呈现：
 *   * pills（默认）—— 三个横排胶囊，用在账号设置页与登录页页脚，那里横向空间充裕
 *   * menu —— 整行 + 右侧 ✓，用在顶栏下拉里。两个理由：
 *       1. 与同一个菜单里的「切换人员」一致——同一个下拉里两组多选项长得不一样很怪
 *       2. 胶囊实测中文态占 198px，而下拉内宽只有 200px（w-56 减去左右 px-3）。
 *          能塞进去，但只剩 2px 余量：换个字、换个平台的字体度量、出个滚动条就溢出。
 *          整行没有这个问题，顺带在手机上点击目标也更大。
 */
export default function LocaleSwitcher({
  variant = 'pills',
  withAuto = false,
}: { variant?: 'pills' | 'menu'; withAuto?: boolean }) {
  const { locale, locale_setting } = usePage<SharedProps>().props
  const switchTo = (value: LocaleSetting) => router.patch('/locale', { locale: value })

  // 只有账号设置那一处给「自动」——它是写进 users.locale 的**偏好**。
  // 右上角是「现在用哪种语言」的快速切换，摆一个「自动」在那儿只会让人犹豫。
  const options = withAuto ? ALL_OPTIONS : ALL_OPTIONS.filter((o) => o.value !== 'auto')

  // 勾哪一行，两处语义不同：
  //   * 带「自动」时勾**存的偏好**（locale_setting）——那才是这个设置项的值
  //   * 只有两种语言时勾**当前生效的语言**（locale）——否则设成「自动」的用户
  //     两行都不会被勾上，看起来像没设置过
  const selected = withAuto ? locale_setting : locale

  if (variant === 'menu') {
    return (
      <>
        {options.map(({ value, label }) => {
          const active = selected === value
          return (
            <button
              key={value}
              type="button"
              onClick={() => switchTo(value)}
              aria-pressed={active}
              className={
                'w-full text-left px-3 py-2 flex items-center gap-2 ' +
                (active ? 'bg-bg font-semibold' : 'hover:bg-bg')
              }
            >
              {label()}
              {active && <span className="ml-auto text-brand">✓</span>}
            </button>
          )
        })}
      </>
    )
  }

  return (
    <div className="flex gap-2">
      {options.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => switchTo(value)}
          aria-pressed={selected === value}
          className={
            'px-3 py-1.5 rounded-lg font-semibold text-sm border transition-colors ' +
            (selected === value
              ? 'bg-tint text-brand border-brand'
              : 'border-line text-muted hover:border-line')
          }
        >
          {label()}
        </button>
      ))}
    </div>
  )
}
